import { useState, useEffect, useRef, useCallback } from 'react';
import { theme } from '../theme';

// Carrusel de imágenes con transición de deslizamiento (la imagen actual sale
// mientras entra la siguiente), autoavance cada `interval` segundos y flechas
// para ir a la anterior/siguiente. Responsive: ocupa el ancho disponible y
// ajusta su alto en móvil.
function Carousel({ images = [], interval = 4 }) {
  const slides = Array.isArray(images) ? images.filter(Boolean) : [];
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const count = slides.length;
  const go = useCallback((next) => {
    if (count === 0) return;
    setIndex(i => ((next % count) + count) % count);
  }, [count]);
  const prev = useCallback(() => go(index - 1), [go, index]);
  const next = useCallback(() => go(index + 1), [go, index]);

  // Autoavance. Se reinicia al cambiar de imagen (manual o automático) y se
  // detiene al pasar el cursor por encima.
  useEffect(() => {
    if (count <= 1 || paused) return;
    const secs = Math.min(60, Math.max(1, Number(interval) || 4));
    timerRef.current = setTimeout(() => { setIndex(i => (i + 1) % count); }, secs * 1000);
    return () => clearTimeout(timerRef.current);
  }, [index, count, interval, paused]);

  // Si la lista de imágenes cambia y el índice queda fuera de rango, lo corregimos.
  useEffect(() => {
    if (index >= count && count > 0) setIndex(0);
  }, [count, index]);

  if (count === 0) return null;

  const arrowBtn = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    width: '40px', height: '40px', borderRadius: '50%',
    background: 'rgba(0,0,0,0.45)', color: '#fff', border: 'none',
    cursor: 'pointer', fontSize: '1.1rem', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 2,
    transition: 'background 0.2s',
  };

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        position: 'relative', width: '100%', overflow: 'hidden',
        borderRadius: '14px', border: `1px solid ${theme.colors.border}`,
        marginBottom: '1.5rem', backgroundColor: theme.colors.surface,
        aspectRatio: '16 / 6',
      }}
    >
      {/* Pista deslizante: todas las imágenes en fila, desplazadas por el índice */}
      <div
        style={{
          display: 'flex', height: '100%', width: '100%',
          transform: `translateX(-${index * 100}%)`,
          transition: 'transform 0.6s ease',
        }}
      >
        {slides.map((src, i) => (
          <div key={i} style={{ flex: '0 0 100%', width: '100%', height: '100%' }}>
            <img
              src={src}
              alt=""
              draggable={false}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </div>
        ))}
      </div>

      {count > 1 && (
        <>
          <button
            type="button" aria-label="Imagen anterior" onClick={prev}
            style={{ ...arrowBtn, left: '10px' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.7)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.45)'; }}
          >‹</button>
          <button
            type="button" aria-label="Imagen siguiente" onClick={next}
            style={{ ...arrowBtn, right: '10px' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.7)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.45)'; }}
          >›</button>

          {/* Indicadores (puntos) */}
          <div style={{
            position: 'absolute', bottom: '10px', left: 0, right: 0,
            display: 'flex', justifyContent: 'center', gap: '7px', zIndex: 2,
          }}>
            {slides.map((_, i) => (
              <button
                key={i} type="button" aria-label={`Ir a la imagen ${i + 1}`}
                onClick={() => setIndex(i)}
                style={{
                  width: i === index ? '22px' : '8px', height: '8px',
                  borderRadius: '4px', border: 'none', padding: 0, cursor: 'pointer',
                  background: i === index ? theme.colors.primary : 'rgba(255,255,255,0.6)',
                  transition: 'width 0.3s, background 0.3s',
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default Carousel;
